<template>
  <v-container fluid grid-list-lg>
    <Create
      :createDialog="createDialog"
      @close="createDialog = false"
      @submit="getList"
    />
    <Edit
      :editDialog="editDialog"
      @close="editDialog = false"
      @submit="getList"
      :editCurriculum="selectedCurriculum"
    />
    <AssignSubject
      :editDialog="assignSubjectDialog"
      :selectedCurriculum="selectedCurriculum"
      @close="assignSubjectClose"
      @submit="assigntSubjectSubmit"
    />

    <v-card :loading="loading" outlined>
      <ListTitle
        :showBackButton="true"
        :access="checkRightStatus(45)"
        title="Curriculum"
        @add="add"
      />

      <v-card-text>
        <v-row align="center">
          <v-col cols="12" md="5">
            <v-text-field
              v-model="search"
              outlined
              prepend-inner-icon="search"
              rounded
              dense
              label="Search curriculum or subject"
              single-line
              hide-details
              clearable
            />
          </v-col>

          <v-col cols="12" md="7" class="text-md-right">
            <v-chip class="mr-2" outlined>
              Total Curriculums: {{ filteredCurriculums.length }}
            </v-chip>
            <v-chip outlined>
              Total Subjects: {{ totalSubjects }}
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-text class="pt-4">
        <v-expansion-panels v-if="filteredCurriculums.length" multiple>
          <v-expansion-panel
            v-for="curriculum in filteredCurriculums"
            :key="curriculum.id"
          >
            <v-expansion-panel-header>
              <div class="d-flex align-center justify-space-between flex-grow-1 pr-3">
                <div>
                  <div class="text-subtitle-1 font-weight-medium text-capitalize">
                    {{ curriculum.name }}
                  </div>
                  <div class="text-caption grey--text" v-if="getSubjectCount(curriculum)">
                    {{ getSubjectCount(curriculum) }} Subject{{ getSubjectCount(curriculum) > 1 ? "s" : "" }},
                    Total Marks: {{ getTotalMarks(curriculum) }}
                  </div>
                  <div class="text-caption grey--text" v-else>
                    No subjects assigned yet
                  </div>
                </div>

                <div class="d-flex align-center flex-wrap justify-end">
                  <v-chip small color="primary" text-color="white" class="mr-2">
                    {{ getSubjectCount(curriculum) }} Subject{{ getSubjectCount(curriculum) > 1 ? "s" : "" }}
                  </v-chip>

                  <v-btn
                    small
                    color="success"
                    class="mr-2"
                    @click.stop="assignSubject(curriculum)"
                  >
                    <v-icon left small>mdi-book-plus</v-icon>
                    Assign Subject
                  </v-btn>

                  <v-menu bottom left offset-y @click.stop="">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        @click.stop
                      >
                        <v-icon>more_vert</v-icon>
                      </v-btn>
                    </template>

                    <v-list class="menu-button">
                      <v-list-item @click="assignSubject(curriculum)">
                        <v-list-item-title>Assign Subject</v-list-item-title>
                      </v-list-item>

                      <v-list-item
                        v-if="checkRightStatus(47)"
                        @click="editCurriculum(curriculum)"
                      >
                        <v-list-item-title>Edit</v-list-item-title>
                      </v-list-item>

                      <v-divider></v-divider>

                      <v-list-item
                        v-if="checkRightStatus(48)"
                        @click="deleteCurriculum(curriculum.id)"
                      >
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content>
              <div
                v-if="
                  curriculum.subjectMeta &&
                  curriculum.subjectMeta.subjects &&
                  curriculum.subjectMeta.subjects.length
                "
              >
                <v-simple-table class="subject-table">
                  <thead>
                    <tr>
                      <th class="text-left">Order</th>
                      <th class="text-left">Subject</th>
                      <th class="text-left">Marks</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(subject, index) in getSortedSubjects(curriculum)"
                      :key="subject.id + '-' + index"
                    >
                      <td>{{ subject.order || index + 1 }}</td>
                      <td class="text-capitalize">{{ subject.name }}</td>
                      <td>{{ subject.marks }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <div v-else class="py-4 d-flex align-center justify-space-between flex-wrap">
                <div class="grey--text text-body-2">
                  No subjects assigned to this curriculum.
                </div>

                <v-btn
                  small
                  color="success"
                  @click="assignSubject(curriculum)"
                >
                  <v-icon left small>mdi-book-plus</v-icon>
                  Assign First Subject
                </v-btn>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-alert
          v-else
          :value="true"
          color="warning"
          icon="warning"
          outlined
        >
          Your search for "{{ search }}" found no results.
        </v-alert>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import curriculumService from "../service";
import { mapGetters } from "vuex";

const Create = () => import("./Create");
const Edit = () => import("./Edit");
const AssignSubject = () =>
  import("@/modules/ReportCard/Curriculums/components/AssignSubject.vue");

export default {
  name: "curriculum-list",
  components: {
    Create,
    Edit,
    AssignSubject,
  },
  data() {
    return {
      createDialog: false,
      editDialog: false,
      selectedCurriculum: null,
      assignSubjectDialog: false,
      curriculums: [],
      search: null,
      loading: true,
    };
  },
  created() {
    this.getList();
    document.title = "taleem - Curriculum List";
  },
  computed: {
    ...mapGetters("global", ["currentUser", "checkRightStatus"]),

    filteredCurriculums() {
      if (!this.search) return this.curriculums;

      const search = this.search.toLowerCase();

      return this.curriculums.filter((curriculum) => {
        const curriculumName = curriculum.name
          ? curriculum.name.toLowerCase()
          : "";

        const subjectNames =
          curriculum.subjectMeta &&
          curriculum.subjectMeta.subjects &&
          curriculum.subjectMeta.subjects.length
            ? curriculum.subjectMeta.subjects
                .map((subject) => (subject.name ? subject.name.toLowerCase() : ""))
                .join(" ")
            : "";

        return (
          curriculumName.includes(search) || subjectNames.includes(search)
        );
      });
    },

    totalSubjects() {
      return this.filteredCurriculums.reduce((total, curriculum) => {
        return total + this.getSubjectCount(curriculum);
      }, 0);
    },
  },
  methods: {
    add() {
      this.createDialog = true;
    },

    async getList() {
      this.loading = true;
      this.editDialog = false;
      this.createDialog = false;

      try {
        const response = await curriculumService.getAll();
        console.log("response", response);
        this.curriculums = response.data || [];
        return response;
      } catch (error) {
        console.log("error getting curriculums", error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    getSubjectCount(curriculum) {
      if (
        curriculum &&
        curriculum.subjectMeta &&
        curriculum.subjectMeta.subjects
      ) {
        return curriculum.subjectMeta.subjects.length;
      }
      return 0;
    },

    getTotalMarks(curriculum) {
      if (
        !curriculum ||
        !curriculum.subjectMeta ||
        !curriculum.subjectMeta.subjects
      ) {
        return 0;
      }

      return curriculum.subjectMeta.subjects.reduce((total, subject) => {
        return total + (parseInt(subject.marks) || 0);
      }, 0);
    },

    getSortedSubjects(curriculum) {
      if (
        !curriculum ||
        !curriculum.subjectMeta ||
        !curriculum.subjectMeta.subjects
      ) {
        return [];
      }

      return [...curriculum.subjectMeta.subjects].sort((a, b) => {
        const orderA = a.order || 9999;
        const orderB = b.order || 9999;
        return orderA - orderB;
      });
    },

    editCurriculum(curriculum) {
      this.selectedCurriculum = curriculum;
      this.editDialog = true;
    },

    deleteCurriculum(id) {
      this.$swal({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#B71C1C",
        cancelButtonColor: "#43A047",
        cancelButtonText: "No",
        confirmButtonText: "Yes",
      }).then((result) => {
        if (result.value) {
          curriculumService.delete(id).then((response) => {
            if (response.data) {
              this.$swal("Deleted!", "Curriculum has been deleted.", "success");
              this.getList();
            }
          });
        }
      });
    },

    assignSubject(curriculum) {
      this.selectedCurriculum = curriculum;
      this.assignSubjectDialog = true;
    },

    assignSubjectClose() {
      this.selectedCurriculum = null;
      this.assignSubjectDialog = false;
    },

    assigntSubjectSubmit() {
      this.assignSubjectDialog = false;
      this.selectedCurriculum = null;
      this.getList();
    },
  },
};
</script>

<style scoped>
.subject-table {
  border: 1px solid #eeeeee;
  border-radius: 8px;
  overflow: hidden;
}
</style>